import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { getArtifactAudience, type ArtifactAudience } from '../transport/relay'
import { Icon } from '../ui/Icon'

/**
 * WHICH FRIENDS MAY OPEN THIS ARTIFACT.
 *
 * Owner, 2026-09-20: 「把下拉選單上面 Friends 那個按鈕改成按下去之後會彈跳出來一個選單
 * 讓使用者可以選擇要把哪些朋友加進去,當然也有一個 checkbox 可以讓使用者把所有的朋友
 * 都勾選,也有另外一個 Button 可以讓所有的朋友都 uncheck」.
 *
 * A POPUP here, deliberately, and not the rail-body treatment the same owner
 * asked for twice the same day: More and Activity are PLACES you go, this is a
 * choice you make about the thing already on screen and then dismiss. Covering
 * that thing is the point.
 *
 * ── THE THREE STATES, because two of them look alike and are not ─────────────
 *
 *   ALL      no list at all. Everyone you are friends with, INCLUDING people
 *            you become friends with later. This is what "friends" meant before
 *            the picker existed, so it is what every artifact already published
 *            means, and nobody has to come here to keep what they had.
 *   SOME     exactly the people ticked. A friend added tomorrow is not among
 *            them — that is the difference from ALL, and it is why ticking every
 *            box is NOT the same as choosing "Everyone".
 *   NOBODY   ticked nothing. A real choice ("Uncheck all"), and the one the
 *            wire must never round off to ALL: `allow.length ? allow : undefined`
 *            would hand the page to every friend at the moment its owner asked
 *            for the opposite.
 *
 * The picker returns `null` for ALL and an array for the other two, which is
 * exactly what artifactShare's `allow` means.
 */
export function FriendPicker({
  /** Who may open it now: `null`/undefined = everyone, an array = these people. */
  value,
  onCancel,
  onSave,
  saving,
  error,
}: {
  value: string[] | null | undefined
  onCancel: () => void
  onSave: (allow: string[] | null) => void
  saving: boolean
  error: string | null
}) {
  const audience = useQuery({
    queryKey: ['artifact-audience'],
    queryFn: getArtifactAudience,
    staleTime: 30_000,
  })
  const friends: ArtifactAudience[] = audience.data?.friends ?? []
  /*
   * ⚠️ NO COUNT BEFORE THE ANSWER. While the fetch is out, `friends` is [] and
   * every number derived from it is a confident wrong one — the first run of
   * this screen said "all 0 friends" over an account that has one, which is the
   * same defect as the More panel's "Nothing published yet" earlier the same
   * day: a conclusion rendered before the thing it describes has happened.
   */
  const settled = !audience.isPending && !audience.isError

  /** ALL is the absence of a list, not a list containing everyone — see above. */
  const [all, setAll] = useState(value === null || value === undefined)
  const [picked, setPicked] = useState<Set<string>>(new Set(value ?? []))

  const toggle = (id: string) => {
    /*
     * Touching one person leaves "Everyone", and cannot not: while ALL is on
     * every box below is drawn ticked, so a click is always an UNtick, and
     * leaving ALL on would show an empty box over a setting that still means
     * everyone — a control visibly disagreeing with itself.
     *
     * Everyone MINUS this one is the honest landing point. Not "nobody except
     * the ones I now tick": the person just said "not them", not "none of them".
     */
    if (all) {
      setAll(false)
      setPicked(new Set(friends.map((f) => f.id).filter((x) => x !== id)))
      return
    }
    setPicked((prev) => {
      const next = new Set(prev)
      if (next.has(id)) next.delete(id)
      else next.add(id)
      return next
    })
  }

  const ticked = (id: string) => all || picked.has(id)
  const count = all ? friends.length : picked.size

  return (
    <div className="fp-scrim" role="dialog" aria-modal="true" aria-label="Who may open it">
      <div className="fp">
        <header className="fp-hd">
          <h3>Choose friends</h3>
          <button type="button" className="fp-x" onClick={onCancel} aria-label="Close">
            <Icon name="close" size={16} label="Close" />
          </button>
        </header>

        <div className="fp-body">
          <label className="fp-row fp-all">
            <input
              type="checkbox"
              checked={all}
              onChange={(e) => {
                setAll(e.target.checked)
                // Leaving ALL by unticking it starts from nobody rather than
                // from everybody — the box was just turned OFF, and filling the
                // list would make it look like nothing happened.
                if (!e.target.checked) setPicked(new Set())
              }}
            />
            <span className="fp-row-main">
              <span className="fp-name">Everyone I&rsquo;m friends with</span>
              <span className="fp-sub">Including anyone I add later</span>
            </span>
          </label>

          <div className="fp-bulk">
            <span className="fp-count">
              {!settled
                ? ''
                : all
                  ? `all ${friends.length} friend${friends.length === 1 ? '' : 's'}`
                  : count === 0
                    ? 'nobody yet'
                    : `${count} chosen`}
            </span>
            <button
              type="button"
              className="fp-bulk-btn"
              onClick={() => {
                setAll(false)
                setPicked(new Set(friends.map((f) => f.id)))
              }}
              /* Not merely "no friends": a list that has not arrived yet would
                 make Check all tick nobody and look like it worked. */
              disabled={!settled || friends.length === 0}
            >
              Check all
            </button>
            <button
              type="button"
              className="fp-bulk-btn"
              onClick={() => {
                setAll(false)
                setPicked(new Set())
              }}
              disabled={!settled || friends.length === 0}
            >
              Uncheck all
            </button>
          </div>

          {audience.isPending && <p className="fp-note">Reading your friends…</p>}
          {audience.isError && (
            <p className="fp-note fp-bad">
              Could not read your friends:{' '}
              {audience.error instanceof Error ? audience.error.message : 'the account did not answer'}
            </p>
          )}
          {settled && friends.length === 0 && (
            <p className="fp-note">
              You have no friends on Avengents yet. Add someone under Humans, then come back and
              choose them here.
            </p>
          )}

          <ul className="fp-list">
            {friends.map((f) => (
              <li key={f.id}>
                <label className="fp-row">
                  <input type="checkbox" checked={ticked(f.id)} onChange={() => toggle(f.id)} />
                  <span className="fp-row-main">
                    <span className="fp-name">{f.name || f.email}</span>
                    {f.name && <span className="fp-sub">{f.email}</span>}
                  </span>
                </label>
              </li>
            ))}
          </ul>

          {/*
            NOBODY is a legal answer and the picker says what it will do rather
            than refusing it — the artifact stays visible to its owner and to
            nobody else, which is a reasonable thing to want and a surprising
            thing to discover by accident.
          */}
          {settled && !all && picked.size === 0 && friends.length > 0 && (
            <p className="fp-note">
              With nobody chosen, only you will be able to open it.
            </p>
          )}
          {error && <p className="fp-note fp-bad">{error}</p>}
        </div>

        <footer className="fp-foot">
          <button type="button" className="fp-btn" onClick={onCancel}>
            Cancel
          </button>
          <button
            type="button"
            className="fp-btn fp-save"
            disabled={saving}
            onClick={() => onSave(all ? null : [...picked])}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </footer>
      </div>
    </div>
  )
}
